package com.banyan.flightviewer.web

import com.banyan.flightviewer.web.state.WebAppContainer
import com.banyan.flightviewer.web.state.preSeedQueryAreaFromDeepLink
import com.banyan.flightviewer.web.state.wireDeepLinkAirportSelection
import com.banyan.flightviewer.web.state.wireDeepLinkSelection
import com.banyan.flightviewer.web.state.wireFlightTrackLoader
import com.banyan.flightviewer.web.state.wireLocationAutoDetect
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch

/**
 * Browser entry point. Initialises the app container (which constructs the
 * shared `AppState`, manager singletons, and `ServerClient`), loads settings
 * from localStorage, kicks off monitoring, and mounts the DOM.
 */
fun main() {
    if (document.readyState.toString() == "loading") {
        window.addEventListener("DOMContentLoaded", { boot() })
    } else {
        boot()
    }
}

private var booted = false

private fun boot() {
    if (booted) return
    booted = true

    val container = WebAppContainer()
    container.settingsManager.loadAllSettings()
    // Auto-detect location on boot if we still hold the NYC default. Fires
    // asynchronously -- monitoring still starts on the default coords and
    // picks up the detected location on the next cycle (or sooner, via the
    // immediate-refresh signal the detect flow triggers on success).
    wireLocationAutoDetect(container)
    // Selection -> track/detail fetch has to be wired before the user can
    // click anything.
    wireFlightTrackLoader(container)

    // Pre-seed the query area from the deep-link target (if any) before
    // the first monitoring cycle fires, so the first ADSBX + FR24
    // bulk-enrich calls hit the right location instead of wasting a
    // `live/flight-positions/full` credit on home-area planes the
    // recipient will never see. Airport lookups are synchronous; flight
    // lookups are one ADSBX round-trip. Monitoring + deep-link handlers
    // run inside the same coroutine so their ordering is preserved.
    container.appState.scope.launch {
        preSeedQueryAreaFromDeepLink(container)
        container.monitoring.start()
        // Wired after monitoring so the flight fulfilment flow has data
        // to watch; airport resolution is synchronous.
        wireDeepLinkSelection(container)
        wireDeepLinkAirportSelection(container)
    }

    val rootEl = document.getElementById("root")
        ?: error("missing #root element")
    mountApp(rootEl, container)
}
